import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaComments, FaTimes, FaPaperPlane, FaCircle, FaSignOutAlt, FaHeadset } from "react-icons/fa";
import { useChat, ChatMessage, ChatRoom, BookingOption, MessageType } from "../../context/ChatContext";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../common/ConfirmDialog";
import { MediaBubble, AttachmentPicker, AudioRecorder, AttachmentPreview, uploadFileToS3 } from "./ChatMedia";

function baseUrl(): string {
    return (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, "");
}

function fmtTime(iso: string): string {
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
}

const ChatWidget: React.FC = () => {
    const confirm = useConfirm();
    const { user } = useAuth();
    const { socket, connected, role, rooms, unreadTotal, aiProcessing, refreshRooms } = useChat();
    const [open, setOpen] = useState(false);
    const [room, setRoom] = useState<ChatRoom | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState("");
    const [typing, setTyping] = useState(false);
    const [otherTyping, setOtherTyping] = useState(false);
    const [sending, setSending] = useState(false);
    const [pendingFile, setPendingFile] = useState<{ file: File; type: MessageType } | null>(null);
    const [uploading, setUploading] = useState(false);
    const [audioActive, setAudioActive] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const typingTimer = useRef<any>(null);
    const otherTypingTimer = useRef<any>(null);

    const activeRoom = useMemo(() => {
        if (role !== "customer") return null;
        return rooms.find((r) => r.status === "open") || null;
    }, [rooms, role]);

    // Sync room from context — but if the user closed it, keep showing the closed
    // state locally until they reopen the widget.
    useEffect(() => {
        if (!room) {
            setRoom(activeRoom);
            return;
        }
        const updated = rooms.find((r) => r.id === room.id);
        if (updated) setRoom(updated);
        else if (activeRoom) setRoom(activeRoom);
    }, [activeRoom, rooms, room]);

    // When the widget is reopened after a chat was ended, clear the stale room
    // so the next open triggers creation of a fresh one.
    useEffect(() => {
        if (open && room?.status === "closed") {
            setRoom(null);
            setMessages([]);
        }
    }, [open, room?.status]);

    // On open: ensure a room exists, load history, join it
    useEffect(() => {
        if (!open || !user) return;
        let cancelled = false;
        (async () => {
            let r = room;
            if (!r) {
                // Create or fetch the open room
                const res = await fetch(`${baseUrl()}/chat/rooms`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: "{}",
                });
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                r = data.room;
                setRoom(r);
                refreshRooms();
            }
            if (!r) return;
            const hist = await fetch(`${baseUrl()}/chat/rooms/${r.id}/messages`, {
                credentials: "include",
            });
            if (!hist.ok) return;
            const data = await hist.json();
            if (cancelled) return;
            setMessages(data.messages || []);
            socket?.emit("join_room", { roomId: r.id });
        })();
        return () => { cancelled = true; };
    }, [open, user?.uid]);

    // Listen for incoming messages
    useEffect(() => {
        if (!socket) return;
        const onNew = (msg: ChatMessage) => {
            if (room && msg.roomId === room.id) {
                setMessages((prev) => [...prev, msg]);
                if (msg.senderRole !== "customer") {
                    socket.emit("mark_read", { roomId: msg.roomId });
                }
            }
        };
        const onTyping = (p: { roomId: number; by: string; typing: boolean }) => {
            if (room && p.roomId === room.id && p.by !== "customer") {
                setOtherTyping(p.typing);
                if (p.typing) {
                    clearTimeout(otherTypingTimer.current);
                    otherTypingTimer.current = setTimeout(() => setOtherTyping(false), 3500);
                }
            }
        };
        socket.on("new_message", onNew);
        socket.on("typing", onTyping);
        return () => {
            socket.off("new_message", onNew);
            socket.off("typing", onTyping);
        };
    }, [socket, room]);

    // Autoscroll
    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, open, otherTyping]);

    const sendMessage = (text?: string) => {
        const body = (text || draft).trim();
        if (!body || !socket) return;
        setSending(true);
        socket.emit(
            "send_message",
            { roomId: room?.id, body },
            (ack: any) => {
                setSending(false);
                if (ack?.ok && !text) setDraft("");
                else if (!ack?.ok) console.warn("[chat] send failed:", ack?.error);
            },
        );
    };

    const sendAttachment = async (file: File, messageType: MessageType) => {
        if (!socket) {
            setPendingFile(null);
            alert("Not connected. Please wait and try again.");
            return;
        }
        setUploading(true);
        try {
            const { key } = await uploadFileToS3({
                file,
                roomId: room?.id,
                messageType,
            });

            socket.emit(
                "send_message",
                {
                    roomId: room?.id,
                    body: "",
                    messageType,
                    attachmentKey: key,
                    attachmentName: file.name,
                    attachmentSize: file.size,
                    attachmentMime: file.type,
                },
                (ack: any) => {
                    if (!ack?.ok) console.warn("[chat] attachment send failed:", ack?.error);
                },
            );
        } catch (err: any) {
            console.error("[chat] upload failed:", err);
            alert(err.message || "Failed to upload file");
        } finally {
            setUploading(false);
            setPendingFile(null);
        }
    };

    const handleFilePicked = (file: File, type: MessageType) => {
        setPendingFile({ file, type });
    };

    const handleAudioRecorded = (blob: Blob) => {
        // Normalize MIME: MediaRecorder may produce "audio/webm;codecs=opus"
        // but the server only accepts the base MIME (e.g. "audio/webm").
        const mime = (blob.type || "audio/webm").split(";")[0];
        const ext = mime.endsWith("webm") ? "webm" : mime.endsWith("mp4") ? "m4a" : "ogg";
        const file = new File([blob], `voice-message.${ext}`, { type: mime });
        sendAttachment(file, "audio");
    };

    const handleSendPendingFile = () => {
        if (!pendingFile) return;
        sendAttachment(pendingFile.file, pendingFile.type);
    };

    const handleOptionClick = (option: BookingOption) => {
        if (!socket || !room) return;
        sendMessage(option.replyText);
    };

    const endChat = async () => {
        if (!room) return;
        if (!(await confirm({
            title: "End this chat?",
            message: "You can start a new conversation any time.",
            confirmLabel: "End chat",
        }))) return;
        try {
            await fetch(`${baseUrl()}/chat/rooms/${room.id}/close`, {
                method: "POST",
                credentials: "include",
            });
        } catch (err) {
            console.error("[chat] failed to end chat:", err);
        }
        setOpen(false);
        setRoom(null);
        setMessages([]);
    };

    const escalateChat = async () => {
        if (!room) return;
        if (!(await confirm({
            title: "Talk to a team member?",
            message: "We'll connect you to someone as soon as possible.",
            confirmLabel: "Yes, connect me",
        }))) return;
        try {
            const res = await fetch(`${baseUrl()}/chat/rooms/${room.id}/escalate`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason: "Customer requested human support" }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                console.error("[chat] escalate failed:", data.error);
            }
        } catch (err) {
            console.error("[chat] failed to escalate:", err);
        }
    };

    const handleChange = (v: string) => {
        setDraft(v);
        if (!room || !socket) return;
        if (!typing) {
            socket.emit("typing", { roomId: room.id, typing: true });
            setTyping(true);
        }
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => {
            socket.emit("typing", { roomId: room.id, typing: false });
            setTyping(false);
        }, 1200);
    };

    // Hide entirely for staff (they use the inbox page) and unauthenticated users
    if (!user || role === "staff") return null;

    return (
        <>
            {/* Floating button */}
            {!open && (
                <button
                    aria-label="Open support chat"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-white shadow-xl flex items-center justify-center hover:scale-105 transition"
                >
                    <FaComments size={22} />
                    {unreadTotal > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-white">
                            {unreadTotal > 9 ? "9+" : unreadTotal}
                        </span>
                    )}
                </button>
            )}

            {/* Chat panel */}
            {open && (
                <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-80px)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
                    {/* Header */}
                    <div className="px-4 py-3 bg-primary text-white flex items-center justify-between">
                        <div>
                            <div className="font-semibold text-sm flex items-center gap-2">
                                <FaComments /> Support
                            </div>
                            <div className="text-[11px] opacity-80 flex items-center gap-1.5">
                                <FaCircle size={6} className={connected ? "text-green-300" : "text-gray-300"} />
                                {room?.status === "closed"
                                    ? "Chat ended"
                                    : room?.escalated
                                        ? "Connected to team"
                                        : room?.aiMode
                                            ? "AI Assistant — instant replies"
                                            : connected
                                                ? "Online — we usually reply within minutes"
                                                : "Connecting…"}
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {room && room.status === "open" && room.aiMode && !room.escalated && (
                                <button
                                    onClick={escalateChat}
                                    aria-label="Request human support"
                                    title="Talk to a person"
                                    className="h-8 px-2.5 rounded-full hover:bg-white/20 flex items-center gap-1 text-[11px] font-semibold"
                                >
                                    <FaHeadset size={11} />
                                    Human
                                </button>
                            )}
                            {room && room.status === "open" && (
                                <button
                                    onClick={endChat}
                                    aria-label="End chat"
                                    title="End chat"
                                    className="h-8 px-2.5 rounded-full hover:bg-white/20 flex items-center gap-1 text-[11px] font-semibold"
                                >
                                    <FaSignOutAlt size={11} />
                                    End
                                </button>
                            )}
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Minimize chat"
                                className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"
                            >
                                <FaTimes size={14} />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 bg-[#F7F9FC] space-y-2">
                        {messages.length === 0 && (
                            <div className="text-center text-xs text-gray-500 py-8">
                                <div className="inline-block bg-white border border-gray-200 px-4 py-3 rounded-xl shadow-sm">
                                    👋 Hi! How can we help you today?
                                </div>
                            </div>
                        )}
                        {messages.map((m) => {
                            const mine = m.senderRole === "customer";
                            const isBot = m.senderRole === "bot";
                            const isMedia = m.messageType && m.messageType !== "text";
                            return (
                                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm leading-snug whitespace-pre-wrap break-words ${
                                        mine
                                            ? "bg-primary text-white rounded-br-sm"
                                            : isBot
                                                ? "bg-indigo-50 text-gray-800 border border-indigo-200 rounded-bl-sm"
                                                : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm"
                                    }`}>
                                        {isBot && (
                                            <div className="text-[10px] font-bold text-indigo-600 mb-0.5 flex items-center gap-1">
                                                <span className="w-3 h-3 rounded-full bg-indigo-100 flex items-center justify-center text-[7px]">AI</span>
                                                {m.senderName || "Assistant"}
                                            </div>
                                        )}
                                        {!mine && !isBot && m.senderName && (
                                            <div className="text-[10px] font-bold text-primary/80 mb-0.5">{m.senderName}</div>
                                        )}
                                        {/* Media content */}
                                        {isMedia ? (
                                            <MediaBubble message={m} mine={mine} />
                                        ) : (
                                            m.body
                                        )}
                                        {/* Caption for media with text */}
                                        {isMedia && m.body && (
                                            <div className="mt-1">{m.body}</div>
                                        )}
                                        {(isBot && m.metadata?.options?.length) && (
                                            <div className="mt-2 space-y-1.5">
                                                {m.metadata!.options!.map((opt: BookingOption, i: number) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => handleOptionClick(opt)}
                                                        className="w-full text-left px-3 py-2 rounded-xl bg-white border border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 transition text-xs flex items-center justify-between group"
                                                    >
                                                        <div>
                                                            <div className="font-semibold text-gray-800">{opt.label}</div>
                                                            <div className="text-[10px] text-gray-400">
                                                                PNR {opt.pnr} · {opt.status}
                                                            </div>
                                                        </div>
                                                        <span className="text-[10px] font-bold text-indigo-500 opacity-0 group-hover:opacity-100 transition">
                                                            Select →
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        <div className={`text-[9px] mt-1 ${mine ? "text-white/70" : "text-gray-400"}`}>
                                            {fmtTime(m.createdAt)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {(otherTyping || aiProcessing) && (
                            <div className="flex justify-start">
                                <div className="bg-indigo-50 border border-indigo-200 px-3 py-2 rounded-2xl text-xs text-indigo-500">
                                    {aiProcessing ? "AI is thinking" : "Typing"}
                                    <span className="inline-flex gap-1 ml-1 align-middle">
                                        <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" />
                                        <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-.15s]" />
                                        <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-.3s]" />
                                    </span>
                                </div>
                            </div>
                        )}
                        {room?.escalated && room?.status === "open" && (
                            <div className="text-center py-2">
                                <span className="inline-block text-[10px] font-semibold bg-amber-100 text-amber-700 px-3 py-1 rounded-full">
                                    Chat transferred to a team member
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Attachment preview */}
                    {pendingFile && (
                        <AttachmentPreview
                            file={pendingFile.file}
                            type={pendingFile.type}
                            uploading={uploading}
                            onCancel={() => setPendingFile(null)}
                        />
                    )}

                    {/* Composer */}
                    {room?.status === "closed" ? (
                        <div className="border-t border-gray-200 bg-gray-50 px-3 py-3 text-center text-xs text-gray-500">
                            This chat was ended. Open the chat again to start a new conversation.
                        </div>
                    ) : pendingFile && !uploading ? (
                        <div className="border-t border-gray-200 bg-white px-3 py-2.5 flex gap-2">
                            <input
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder="Add a caption…"
                                className="flex-1 text-sm px-3 py-2 rounded-full bg-gray-100 focus:bg-white focus:ring-2 focus:ring-primary/40 outline-none"
                            />
                            <button
                                type="button"
                                onClick={handleSendPendingFile}
                                className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-blue-900 transition"
                                aria-label="Send attachment"
                            >
                                <FaPaperPlane size={13} />
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                            className="border-t border-gray-200 bg-white px-3 py-2.5 flex gap-2 items-center"
                        >
                            {!audioActive && (
                                <AttachmentPicker
                                    onFilePicked={handleFilePicked}
                                    disabled={!connected || sending || uploading}
                                    compact
                                />
                            )}
                            {audioActive ? (
                                /* When recording, let the AudioRecorder controls
                                   take the full width — the input & picker are hidden. */
                                <AudioRecorder
                                    onRecorded={handleAudioRecorded}
                                    disabled={!connected || sending || uploading}
                                    compact
                                    onRecordingChange={setAudioActive}
                                />
                            ) : (
                                <>
                                    <input
                                        value={draft}
                                        onChange={(e) => handleChange(e.target.value)}
                                        placeholder="Type a message…"
                                        className="flex-1 text-sm px-3 py-2 rounded-full bg-gray-100 focus:bg-white focus:ring-2 focus:ring-primary/40 outline-none"
                                        disabled={!connected || sending || uploading}
                                    />
                                    {draft.trim() ? (
                                        <button
                                            type="submit"
                                            aria-label="Send message"
                                            disabled={!draft.trim() || sending || !connected}
                                            className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-50 hover:bg-blue-900 transition"
                                        >
                                            <FaPaperPlane size={13} />
                                        </button>
                                    ) : (
                                        <AudioRecorder
                                            onRecorded={handleAudioRecorded}
                                            disabled={!connected || sending || uploading}
                                            compact
                                            onRecordingChange={setAudioActive}
                                        />
                                    )}
                                </>
                            )}
                        </form>
                    )}
                </div>
            )}
        </>
    );
};

export default ChatWidget;
